import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
	Button,
	Form,
	Input,
	Label,
	FormGroup,
	FormFeedback,
} from "reactstrap";
import NumberFormat from "react-number-format";
import {
	getVehicleInputProperties,
	rateVehicle,
} from "../../../../../../api/TecAssuredAPI";
import { useFromUser } from "../../../../../../stores/LocalStorageHelper";
import dayjs from "dayjs";
import { TecAssuredStore } from "../../../../../../stores/TecAssuredStore";
import useBuildDeal from "../../../../hooks/useBuildDeal";
import {
	TecAssuredVehicleInputMap,
	getValueByPath,
} from "./TecAssuredVehicleInputMap";

const TecAssuredDynamicForm = ({ onComplete }) => {
	const [formConfig, setFormConfig] = useState([]);
	const [vehicleTypeId, setVehicleTypeId] = useState("");
	const [formKey, setFormKey] = useState(0);
	const types = JSON.parse(sessionStorage.getItem("tecAssuredVehicleTypes"));
	const locationID = useFromUser("locationID");
	const { setQuoteData, setCreateAgreementReqBody } = TecAssuredStore();

	const getDeal = useBuildDeal();

	const safeName = (prop) => prop.replace(/\./g, "__");

	const {
		register,
		handleSubmit,
		reset,
		control,
		setValue,
		trigger,
		formState: { errors, isSubmitting },
	} = useForm({
		mode: "onBlur",
	});

	const onSubmit = (formData) => {
		const dataWithOriginalKeys = Object.entries(formData).reduce(
			(acc, [key, value]) => {
				const originalKey = key.replace(/__/g, ".");
				acc[originalKey] = value;
				return acc;
			},
			{}
		);

		const updated = formConfig.map((field) => {
			const { Property, PropertyType } = field;
			const rawKey = Property;
			const value = dataWithOriginalKeys[rawKey];

			let formattedValue = value;

			if (PropertyType === "Date" && value) {
				formattedValue = dayjs(value).utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
			}

			if (PropertyType === "Period") {
				const y = Number(formData[`${Property}_years`] || 0);
				const m = Number(formData[`${Property}_months`] || 0);
				const d = Number(formData[`${Property}_days`] || 0);
				formattedValue = `P${y}Y${m}M${d}D`;
			}

			return {
				...field,
				Value: formattedValue,
			};
		});

		console.log("Submitted:", {
			item: updated,
		});

		const requestBody = {
			// locationId,
			locationId: locationID,
			vehicleTypeId: parseInt(vehicleTypeId),
			item: updated,
		};

		rateVehicle(requestBody).then((res) => {
			console.log(res.data);
			setQuoteData(res.data);
			setCreateAgreementReqBody({
				locationId: locationID,
				RatePackage: res.data,
			});
			if (onComplete) onComplete(); // move to next tab
		});
	};

	useEffect(() => {
		if (!vehicleTypeId) return;

		const request = {
			// locationId,
			locationId: locationID,
			vehicleTypeId: parseInt(vehicleTypeId),
		};

		getVehicleInputProperties(request).then((res) => {
			const deal = getDeal();
			const vTypeId = parseInt(vehicleTypeId);
			const tpaName = res.data.tpaDetails.tpaName;
			const mapping = TecAssuredVehicleInputMap?.[tpaName]?.[vTypeId] || [];
			const defaultValues = {};

			mapping.forEach((mapItem) => {
				const field = res.data.item.find(
					(f) => f.Property === mapItem.Property
				);
				if (field) {
					let value = getValueByPath(deal, mapItem.DC);
					if (typeof mapItem.transform === "function") {
						value = mapItem.transform(value, deal);
					}
					if (value !== undefined) {
						defaultValues[mapItem.Property] = value;
					}
				}
			});

			const safeDefaults = Object.fromEntries(
				Object.entries(defaultValues).map(([key, value]) => [
					safeName(key),
					value,
				])
			);

			console.log("Default form values:", defaultValues);
			setFormConfig(res.data?.item || []);
			reset(safeDefaults);
			setFormKey((prev) => prev + 1);
		});
	}, [vehicleTypeId, reset]);

	useEffect(() => {
		formConfig.forEach((field) => {
			const fieldName = safeName(field.Property);
			const isRequired = field.Required === "true";
			register({ name: fieldName }, { required: isRequired });
		});
	}, [formConfig, register]);

	const renderField = (field) => {
		const { Property, Name, PropertyType, Extension, Required } = field;
		const fieldName = safeName(Property);
		const isRequired = Required === "true";

		// Dropdown
		if (Extension?.Values?.length > 0) {
			return (
				<FormGroup key={fieldName}>
					<Label for={fieldName}>{Name}</Label>
					<Controller
						control={control}
						name={fieldName}
						rules={{ required: isRequired }}
						render={({ onChange, value, onBlur }) => (
							<Input
								type="select"
								id={fieldName}
								value={value || ""}
								onChange={(e) => onChange(e.target.value)}
								onBlur={onBlur}
								invalid={!!errors[fieldName]}
							>
								<option value="">Select {Name}</option>
								{Extension.Values.map((opt) => (
									<option key={opt.Key} value={opt.Value}>
										{opt.Key}
									</option>
								))}
							</Input>
						)}
					/>
					{errors[fieldName] && (
						<FormFeedback>{Name} is required.</FormFeedback>
					)}
				</FormGroup>
			);
		}

		if (PropertyType === "Boolean") {
			return (
				<FormGroup check>
					<Label check>
						<Input
							type="checkbox"
							name={Property}
							innerRef={register({ required: isRequired })}
							onChange={(e) => {
								setValue(Property, e.target.checked);
								trigger(Property);
							}}
							invalid={!!errors[Property]}
						/>
						{` ${Name}`}
					</Label>
					{errors[Property] && (
						<FormFeedback className="d-block">
							{`${Name} is required`}
						</FormFeedback>
					)}
				</FormGroup>
			);
		}

		if (PropertyType === "Decimal") {
			return (
				<FormGroup key={fieldName}>
					<Label for={fieldName}>{Name}</Label>
					<Controller
						control={control}
						name={fieldName}
						rules={{ required: isRequired }}
						render={({ onChange, value }) => (
							<NumberFormat
								id={fieldName}
								customInput={Input}
								thousandSeparator
								decimalScale={6}
								fixedDecimalScale
								placeholder={Name}
								value={value || ""}
								onValueChange={(val) => onChange(val.floatValue ?? 0)}
								className={`form-control ${
									errors[fieldName] ? "is-invalid" : ""
								}`}
							/>
						)}
					/>
					{errors[fieldName] && (
						<FormFeedback>{Name} is required.</FormFeedback>
					)}
				</FormGroup>
			);
		}

		// Currency
		if (PropertyType === "Currency") {
			return (
				<FormGroup key={fieldName}>
					<Label for={fieldName}>{Name}</Label>
					<Controller
						control={control}
						name={fieldName}
						rules={{ required: isRequired }}
						render={({ onChange, value }) => (
							<NumberFormat
								id={fieldName}
								customInput={Input}
								thousandSeparator
								prefix="$"
								decimalScale={2}
								fixedDecimalScale
								placeholder={Name}
								value={value || ""}
								onValueChange={(val) => onChange(val.floatValue ?? 0)}
								className={`form-control ${
									errors[fieldName] ? "is-invalid" : ""
								}`}
							/>
						)}
					/>
					{errors[fieldName] && (
						<FormFeedback>{Name} is required.</FormFeedback>
					)}
				</FormGroup>
			);
		}

		if (PropertyType === "Period") {
			return (
				<div className="d-flex gap-2">
					<Input
						type="number"
						min="0"
						name={`${Property}_years`}
						innerRef={register({ required: isRequired })}
						placeholder="Years"
						invalid={!!errors[`${Property}_years`]}
					/>
					<Input
						type="number"
						min="0"
						name={`${Property}_months`}
						innerRef={register({ required: isRequired })}
						placeholder="Months"
						invalid={!!errors[`${Property}_months`]}
					/>
					<Input
						type="number"
						min="0"
						name={`${Property}_days`}
						innerRef={register({ required: isRequired })}
						placeholder="Days"
						invalid={!!errors[`${Property}_days`]}
					/>
				</div>
			);
		}

		// Text / Number / Date
		let inputType = "text";
		if (PropertyType === "Integer") inputType = "number";
		if (PropertyType === "Date") inputType = "date";

		return (
			<FormGroup key={fieldName}>
				<Label for={fieldName}>{Name}</Label>
				<Controller
					control={control}
					name={fieldName}
					rules={{
						required: isRequired,
						valueAsNumber: PropertyType === "Integer",
					}}
					render={({ onChange, onBlur, value }) => (
						<Input
							id={fieldName}
							type={inputType}
							value={value || ""}
							onChange={onChange}
							onBlur={onBlur}
							placeholder={Name}
							invalid={!!errors[fieldName]}
						/>
					)}
				/>
				{errors[fieldName] && <FormFeedback>{Name} is required.</FormFeedback>}
			</FormGroup>
		);
	};

	return (
		<Form key={formKey} onSubmit={handleSubmit(onSubmit)} noValidate>
			<FormGroup>
				<Label for="vehicleTypeId">Vehicle Type</Label>
				<Input
					type="select"
					id="vehicleTypeId"
					value={vehicleTypeId}
					onChange={(e) => setVehicleTypeId(e.target.value)}
				>
					<option value="" disabled>
						Select vehicle type
					</option>
					{types?.item?.map((opt) => (
						<option key={opt.VehicleTypeID} value={opt.VehicleTypeID}>
							{opt.Name}
						</option>
					))}
				</Input>
			</FormGroup>

			{formConfig.map((field) => renderField(field))}

			<Button type="submit" color="primary" disabled={isSubmitting}>
				Submit
			</Button>
		</Form>
	);
};

export default TecAssuredDynamicForm;
